import React, { useCallback, useEffect, useMemo, useState } from 'react';
import toast from 'react-hot-toast';
import {
  paymentApi,
  LegacyParkingGateTransactionRow,
  LegacyParkingShiftRevenueRow,
} from '../api/paymentApi';
import { formatVndAmount } from '../utils/vndFormat';
import { AddCasualParkingFeeModal } from '../components/service-fees/AddCasualParkingFeeModal';
import { feeCategoriesApi } from '../api/feeCategoriesApi';
import {
  buildCasualParkingInitialFromShiftRevenue,
  parseLegacyShiftTurnCountDetail,
  type CasualParkingFeeInitialValues,
} from '../utils/legacyShiftCasualParking';

interface Props {
  buildingId: string;
  fromDate: string;
  toDate: string;
}

function normalizeSearchText(value: string): string {
  return value.trim().toLowerCase();
}

function rowMatchesQuery(parts: Array<string | number | null | undefined>, query: string): boolean {
  if (!query) return true;
  const haystack = parts
    .filter((p) => p != null && p !== '')
    .map((p) => String(p).toLowerCase())
    .join(' ');
  return haystack.includes(query);
}

function formatDisplayDateTime(iso: string | null, fallback: string | null): string {
  const raw = fallback?.trim() || iso;
  if (!raw) return '—';
  const d = new Date(raw.includes('T') ? raw : raw.replace(' ', 'T'));
  if (!Number.isNaN(d.getTime())) {
    return d.toLocaleString('vi-VN');
  }
  return raw;
}

export const TransactionReportParkingRevenueTab: React.FC<Props> = ({
  buildingId,
  fromDate,
  toDate,
}) => {
  const [section, setSection] = useState<'shifts' | 'transactions'>('shifts');
  const [shifts, setShifts] = useState<LegacyParkingShiftRevenueRow[]>([]);
  const [transactions, setTransactions] = useState<LegacyParkingGateTransactionRow[]>([]);
  const [loading, setLoading] = useState(false);
  const [expandedShiftId, setExpandedShiftId] = useState<string | null>(null);
  const [shiftSearch, setShiftSearch] = useState('');
  const [transactionSearch, setTransactionSearch] = useState('');
  const [hasCasualParkingCategory, setHasCasualParkingCategory] = useState(false);
  const [casualParkingModalOpen, setCasualParkingModalOpen] = useState(false);
  const [casualParkingInitial, setCasualParkingInitial] = useState<
    CasualParkingFeeInitialValues | undefined
  >(undefined);

  useEffect(() => {
    if (!buildingId) {
      setHasCasualParkingCategory(false);
      return;
    }
    feeCategoriesApi
      .getAll(buildingId)
      .then((list) => setHasCasualParkingCategory(list.some((fc) => fc.feeCode === 'T-VL001')))
      .catch(() => setHasCasualParkingCategory(false));
  }, [buildingId]);

  const load = useCallback(async () => {
    if (!buildingId || !fromDate || !toDate) return;
    setLoading(true);
    try {
      const [shiftRows, txRows] = await Promise.all([
        paymentApi.getLegacyParkingShiftRevenues(buildingId, fromDate, toDate),
        paymentApi.getLegacyParkingGateTransactions(buildingId, fromDate, toDate),
      ]);
      setShifts(shiftRows);
      setTransactions(txRows);
    } catch {
      toast.error('Không tải được doanh thu xe từ cổng');
      setShifts([]);
      setTransactions([]);
    } finally {
      setLoading(false);
    }
  }, [buildingId, fromDate, toDate]);

  useEffect(() => {
    load();
  }, [load]);

  const shiftQuery = normalizeSearchText(shiftSearch);

  const filteredShifts = useMemo(() => {
    if (!shiftQuery) return shifts;
    return shifts.filter((row) =>
      rowMatchesQuery(
        [
          row.shiftCode,
          row.staffName,
          row.startedAtDisplay,
          row.endedAtDisplay,
          formatDisplayDateTime(row.startedAt, row.startedAtDisplay),
          formatDisplayDateTime(row.endedAt, row.endedAtDisplay),
          row.turnCountDetailJson,
        ],
        shiftQuery,
      ),
    );
  }, [shifts, shiftQuery]);

  const transactionQuery = normalizeSearchText(transactionSearch);

  const filteredTransactions = useMemo(() => {
    if (!transactionQuery) return transactions;
    return transactions.filter((row) =>
      rowMatchesQuery(
        [
          row.transactionCode,
          row.cardTypeName,
          row.staffName,
          row.entryAtDisplay,
          row.exitAtDisplay,
          formatDisplayDateTime(row.entryAt, row.entryAtDisplay),
          formatDisplayDateTime(row.exitAt, row.exitAtDisplay),
          row.amount,
        ],
        transactionQuery,
      ),
    );
  }, [transactions, transactionQuery]);

  const shiftTotal = useMemo(
    () => filteredShifts.reduce((sum, row) => sum + row.totalAmount, 0),
    [filteredShifts],
  );

  const transactionTotal = useMemo(
    () => filteredTransactions.reduce((sum, row) => sum + (row.amount ?? 0), 0),
    [filteredTransactions],
  );

  const openCasualParkingFromShift = (row: LegacyParkingShiftRevenueRow) => {
    setCasualParkingInitial(buildCasualParkingInitialFromShiftRevenue(row));
    setCasualParkingModalOpen(true);
  };

  return (
    <div>
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 mb-4">
        <div>
          <h2 className="text-lg font-bold text-gray-900">Doanh thu xe (cổng legacy)</h2>
          <p className="text-sm text-gray-600 mt-1">
            Dữ liệu đồng bộ từ phần mềm cổng (API legacy parking ingest) — ca trực và lịch sử xe ra vào.
          </p>
        </div>
        <div className="inline-flex rounded-lg border border-gray-200 overflow-hidden text-sm">
          <button
            type="button"
            onClick={() => setSection('shifts')}
            className={`px-4 py-2 ${section === 'shifts' ? 'bg-cyan-500 text-white' : 'bg-white text-gray-700 hover:bg-gray-50'}`}
          >
            Doanh thu ca trực ({filteredShifts.length}/{shifts.length})
          </button>
          <button
            type="button"
            onClick={() => setSection('transactions')}
            className={`px-4 py-2 border-l ${section === 'transactions' ? 'bg-cyan-500 text-white' : 'bg-white text-gray-700 hover:bg-gray-50'}`}
          >
            Lịch sử xe ra vào ({filteredTransactions.length}/{transactions.length})
          </button>
        </div>
      </div>

      {loading ? (
        <p className="text-sm text-gray-500 py-8 text-center">Đang tải...</p>
      ) : section === 'shifts' ? (
        <>
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 mb-3">
            <p className="text-sm text-gray-700">
              Tổng tiền ca trực
              {shiftQuery ? ' (đã lọc)' : ' trong kỳ'}:{' '}
              <span className="font-semibold text-gray-900">{formatVndAmount(shiftTotal)}</span>
            </p>
            <div className="flex items-center gap-2">
              <label className="text-sm text-gray-700 whitespace-nowrap">Tìm kiếm:</label>
              <input
                type="text"
                value={shiftSearch}
                onChange={(e) => setShiftSearch(e.target.value)}
                placeholder="Mã ca, nhân viên, thời gian..."
                className="px-3 py-1.5 border border-gray-200 rounded-lg min-w-[220px] text-sm"
              />
            </div>
          </div>
          {shifts.length === 0 ? (
            <p className="text-sm text-amber-800 bg-amber-50 border border-amber-100 rounded-lg px-4 py-3">
              Chưa có ca trực trong khoảng ngày đã chọn. Kiểm tra API key cổng xe trên tòa nhà và
              cấu hình cổng gửi <code className="text-xs">requestType=revenue</code> tới{' '}
              <code className="text-xs">/api/legacy/parking/ingest</code>.
            </p>
          ) : filteredShifts.length === 0 ? (
            <p className="text-sm text-gray-600 bg-gray-50 border border-gray-200 rounded-lg px-4 py-3">
              Không có ca trực khớp từ khóa «{shiftSearch.trim()}».
            </p>
          ) : (
            <div className="overflow-x-auto border border-gray-200 rounded-lg">
              <table className="min-w-full text-sm">
                <thead className="bg-gray-50 text-gray-700">
                  <tr>
                    <th className="px-3 py-2 text-left font-medium">Mã ca</th>
                    <th className="px-3 py-2 text-left font-medium">Nhân viên</th>
                    <th className="px-3 py-2 text-left font-medium">Bắt đầu</th>
                    <th className="px-3 py-2 text-left font-medium">Kết thúc</th>
                    <th className="px-3 py-2 text-right font-medium">Vào</th>
                    <th className="px-3 py-2 text-right font-medium">Ra</th>
                    <th className="px-3 py-2 text-right font-medium">Tồn</th>
                    <th className="px-3 py-2 text-right font-medium">Tổng tiền</th>
                    <th className="px-3 py-2 text-left font-medium">Chi tiết</th>
                    {hasCasualParkingCategory ? (
                      <th className="px-3 py-2 text-left font-medium">Thao tác</th>
                    ) : null}
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100">
                  {filteredShifts.map((row) => (
                    <React.Fragment key={row.id}>
                      <tr className="hover:bg-gray-50">
                        <td className="px-3 py-2 font-mono text-xs">{row.shiftCode}</td>
                        <td className="px-3 py-2">{row.staffName || '—'}</td>
                        <td className="px-3 py-2 whitespace-nowrap">
                          {formatDisplayDateTime(row.startedAt, row.startedAtDisplay)}
                        </td>
                        <td className="px-3 py-2 whitespace-nowrap">
                          {formatDisplayDateTime(row.endedAt, row.endedAtDisplay)}
                        </td>
                        <td className="px-3 py-2 text-right">{row.totalEntries}</td>
                        <td className="px-3 py-2 text-right">{row.totalExits}</td>
                        <td className="px-3 py-2 text-right">{row.totalOnSite}</td>
                        <td className="px-3 py-2 text-right font-medium">
                          {formatVndAmount(row.totalAmount)}
                        </td>
                        <td className="px-3 py-2">
                          {row.turnCountDetailJson ? (
                            <button
                              type="button"
                              className="text-cyan-600 hover:underline text-xs"
                              onClick={() =>
                                setExpandedShiftId((prev) => (prev === row.id ? null : row.id))
                              }
                            >
                              {expandedShiftId === row.id ? 'Thu gọn' : 'Số lượt xe'}
                            </button>
                          ) : (
                            '—'
                          )}
                        </td>
                        {hasCasualParkingCategory ? (
                          <td className="px-3 py-2">
                            <button
                              type="button"
                              onClick={() => openCasualParkingFromShift(row)}
                              className="text-xs font-medium text-cyan-700 hover:text-cyan-900 whitespace-nowrap"
                            >
                              Tạo phiếu thu
                            </button>
                          </td>
                        ) : null}
                      </tr>
                      {expandedShiftId === row.id && row.turnCountDetailJson && (
                        <tr>
                          <td
                            colSpan={hasCasualParkingCategory ? 10 : 9}
                            className="px-3 py-2 bg-gray-50"
                          >
                            {(() => {
                              const lines = parseLegacyShiftTurnCountDetail(row.turnCountDetailJson);
                              if (lines.length === 0) {
                                return (
                                  <p className="text-xs text-gray-600 whitespace-pre-wrap break-all">
                                    {row.turnCountDetailJson}
                                  </p>
                                );
                              }
                              return (
                                <table className="min-w-[320px] text-xs text-gray-800 border border-gray-200 rounded overflow-hidden">
                                  <thead className="bg-gray-100">
                                    <tr>
                                      <th className="px-2 py-1.5 text-left font-medium">Loại thẻ</th>
                                      <th className="px-2 py-1.5 text-right font-medium">Xe vào</th>
                                      <th className="px-2 py-1.5 text-right font-medium">Xe ra</th>
                                      <th className="px-2 py-1.5 text-right font-medium">Doanh thu</th>
                                    </tr>
                                  </thead>
                                  <tbody>
                                    {lines.map((line, index) => (
                                      <tr key={`${line.loaiThe}-${index}`} className="border-t border-gray-200">
                                        <td className="px-2 py-1.5">{line.loaiThe}</td>
                                        <td className="px-2 py-1.5 text-right">{line.soXeVao}</td>
                                        <td className="px-2 py-1.5 text-right">{line.soXeRa}</td>
                                        <td className="px-2 py-1.5 text-right">
                                          {formatVndAmount(line.soTien)}
                                        </td>
                                      </tr>
                                    ))}
                                  </tbody>
                                </table>
                              );
                            })()}
                          </td>
                        </tr>
                      )}
                    </React.Fragment>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </>
      ) : (
        <>
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 mb-3">
            <p className="text-sm text-gray-700">
              Tổng tiền giao dịch
              {transactionQuery ? ' (đã lọc)' : ''}:{' '}
              <span className="font-semibold text-gray-900">{formatVndAmount(transactionTotal)}</span>
            </p>
            <div className="flex items-center gap-2">
              <label className="text-sm text-gray-700 whitespace-nowrap">Tìm kiếm:</label>
              <input
                type="text"
                value={transactionSearch}
                onChange={(e) => setTransactionSearch(e.target.value)}
                placeholder="Mã GD, loại thẻ, nhân viên..."
                className="px-3 py-1.5 border border-gray-200 rounded-lg min-w-[220px] text-sm"
              />
            </div>
          </div>
          {transactions.length === 0 ? (
            <p className="text-sm text-amber-800 bg-amber-50 border border-amber-100 rounded-lg px-4 py-3">
              Chưa có lượt ra vào trong khoảng ngày đã chọn. Cổng gửi{' '}
              <code className="text-xs">requestType=transaction</code> qua{' '}
              <code className="text-xs">/api/legacy/parking/ingest</code>.
            </p>
          ) : filteredTransactions.length === 0 ? (
            <p className="text-sm text-gray-600 bg-gray-50 border border-gray-200 rounded-lg px-4 py-3">
              Không có lượt ra vào khớp từ khóa «{transactionSearch.trim()}».
            </p>
          ) : (
            <div className="overflow-x-auto border border-gray-200 rounded-lg">
              <table className="min-w-full text-sm">
                <thead className="bg-gray-50 text-gray-700">
                  <tr>
                    <th className="px-3 py-2 text-left font-medium">Mã GD</th>
                    <th className="px-3 py-2 text-left font-medium">Loại thẻ</th>
                    <th className="px-3 py-2 text-left font-medium">Giờ vào</th>
                    <th className="px-3 py-2 text-left font-medium">Giờ ra</th>
                    <th className="px-3 py-2 text-right font-medium">Số tiền</th>
                    <th className="px-3 py-2 text-left font-medium">Nhân viên</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100">
                  {filteredTransactions.map((row) => (
                    <tr key={row.id} className="hover:bg-gray-50">
                      <td className="px-3 py-2 font-mono text-xs">{row.transactionCode}</td>
                      <td className="px-3 py-2">{row.cardTypeName || '—'}</td>
                      <td className="px-3 py-2 whitespace-nowrap">
                        {formatDisplayDateTime(row.entryAt, row.entryAtDisplay)}
                      </td>
                      <td className="px-3 py-2 whitespace-nowrap">
                        {formatDisplayDateTime(row.exitAt, row.exitAtDisplay)}
                      </td>
                      <td className="px-3 py-2 text-right">
                        {row.amount != null ? formatVndAmount(row.amount) : '—'}
                      </td>
                      <td className="px-3 py-2">{row.staffName || '—'}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </>
      )}

      <AddCasualParkingFeeModal
        buildingId={buildingId}
        isOpen={casualParkingModalOpen}
        onClose={() => {
          setCasualParkingModalOpen(false);
          setCasualParkingInitial(undefined);
        }}
        initialValues={casualParkingInitial}
        onSuccess={() => {
          setCasualParkingModalOpen(false);
          setCasualParkingInitial(undefined);
        }}
      />
    </div>
  );
};
